// src/components/react-dashboard/data_assets/release/utils.js
export const daysOldFromDate = (dateValue) => {
  if (!dateValue) return null;
  const ms = new Date().getTime() - new Date(dateValue).getTime();
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
};

export const ageChipSx = (days, colors) => {
  if (days == null) {
    return { bgcolor: colors.grey[700], color: colors.grey[100] };
  }
  if (days <= 30) {
    return { bgcolor: colors.greenAccent[700], color: colors.grey[100] };
  }
  if (days < 60) {
    return { bgcolor: colors.blueAccent[700], color: colors.grey[100] };
  }
  return { bgcolor: colors.redAccent[700], color: colors.grey[100] };
};

export const formatTrackDate = (dateValue) => {
  if (!dateValue) return 'Not scheduled';
  return new Date(dateValue).toLocaleString();
};

export const normalizeS3Base = (base) => {
  if (!base) return '';
  let out = String(base).trim();
  out = out.replace(/^s3:\/\//i, '');
  out = out.replace(/\/+$/, '');
  return out;
};

export const normalizeVersionFolder = (version) =>
  String(version || '').trim().replace(/\./g, '_');

export const normalizeProductFolder = (productName) =>
  String(productName || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

export const getOrgInfra = (org) => {
  const infra = org?.s3_config?.infra_data || {};
  const onprem = infra?.onprem_config || {};
  const snowflake = infra?.snowflake_config || {};

  const s3Base = onprem?.s3_uri || onprem?.onprem_bucket || org?.s3_uri || '';

  return {
    rawS3Base: s3Base,
    normalizedS3Base: normalizeS3Base(s3Base),
    hasS3: Boolean(s3Base),
    snowflakeDb: snowflake?.database || '',
    snowflakeSchema: snowflake?.schema || '',
    hasSnowflake: Boolean(snowflake?.database && snowflake?.schema),
  };
};

export const getHistoricalDeliveryModes = (org, productName) => {
  const current = Array.isArray(org?.current_delivery) ? org.current_delivery : [];
  const matching = current.filter((cd) => cd?.product === productName);

  const modes = new Set();
  for (const row of matching) {
    if (row?.delivery_target_type === 'snowflake') modes.add('snowflake');
    if (row?.delivery_target_type === 's3') {
      if (row?.file_format === 'csv') modes.add('csv');
      if (row?.file_format === 'parquet') modes.add('parquet');
    }
  }

  return {
    modes: Array.from(modes),
    history: matching,
    latestVersion: matching.length
      ? matching
          .slice()
          .sort(
            (a, b) =>
              new Date(b?.delivery_date_utc || 0).getTime() -
              new Date(a?.delivery_date_utc || 0).getTime()
          )[0]?.version || ''
      : '',
  };
};

export const getRequestedModesFromOrg = (org) => {
  const raw = Array.isArray(org?.delivery_format) ? org.delivery_format : [];
  const normalized = raw.map((x) => String(x || '').trim().toLowerCase()).filter(Boolean);

  const modes = new Set();
  for (const v of normalized) {
    if (v === 'csv') modes.add('csv');
    if (v === 'parquet') modes.add('parquet');
    if (v === 'snowflake') modes.add('snowflake');
  }

  return Array.from(modes);
};

export const buildS3DeliveryUri = ({ base, productName, version, format }) => {
  const normalizedBase = normalizeS3Base(base);
  if (!normalizedBase) return '';

  const productFolder = normalizeProductFolder(productName);
  const versionFolder = normalizeVersionFolder(version);

  if (format === 'parquet') {
    return `s3://${normalizedBase}/outgoing/${productFolder}_${versionFolder}_parquet/`;
  }

  return `s3://${normalizedBase}/outgoing/${productFolder}_${versionFolder}/`;
};

export const deriveDeliveryPlan = ({ org, planned, versionMeta }) => {
  const infra = getOrgInfra(org);
  const history = getHistoricalDeliveryModes(org, planned.product);
  const requestedModes = getRequestedModesFromOrg(org);

  let source = 'history';
  let desiredModes = history.modes;

  if (!desiredModes.length) {
    desiredModes = requestedModes;
    source = 'delivery_format';
  }

  if (!desiredModes.length) {
    if (infra.hasS3) {
      desiredModes = ['parquet'];
      source = 'fallback_s3';
    } else if (infra.hasSnowflake) {
      desiredModes = ['snowflake'];
      source = 'fallback_snowflake';
    } else {
      desiredModes = [];
      source = 'unresolved';
    }
  }

  const deliveries = [];
  const warnings = [];

  const availability = {
    hasCsv: Array.isArray(versionMeta?.s3_locations) && versionMeta.s3_locations.some((x) => x?.format === 'csv'),
    hasParquet: Array.isArray(versionMeta?.s3_locations) && versionMeta.s3_locations.some((x) => x?.format === 'parquet'),
    hasSnowflake: Boolean(versionMeta?.snowflake_table),
  };

  for (const mode of desiredModes) {
    if (mode === 'csv') {
      if (!infra.hasS3) {
        warnings.push('CSV requested but org has no S3 delivery config');
        continue;
      }
      if (!availability.hasCsv) warnings.push('Selected version has no CSV location metadata');

      deliveries.push({
        mode: 'csv',
        delivery_target_type: 's3',
        file_format: 'csv',
        destination_uri: buildS3DeliveryUri({
          base: infra.rawS3Base,
          productName: planned.product,
          version: planned.version,
          format: 'csv',
        }),
      });
    }

    if (mode === 'parquet') {
      if (!infra.hasS3) {
        warnings.push('Parquet requested but org has no S3 delivery config');
        continue;
      }
      if (!availability.hasParquet) warnings.push('Selected version has no parquet location metadata');

      deliveries.push({
        mode: 'parquet',
        delivery_target_type: 's3',
        file_format: 'parquet',
        destination_uri: buildS3DeliveryUri({
          base: infra.rawS3Base,
          productName: planned.product,
          version: planned.version,
          format: 'parquet',
        }),
      });
    }

    if (mode === 'snowflake') {
      if (!infra.hasSnowflake) {
        warnings.push('Snowflake requested but org has no Snowflake config');
        continue;
      }
      if (!availability.hasSnowflake) warnings.push('Selected version has no Snowflake table metadata');

      deliveries.push({
        mode: 'snowflake',
        delivery_target_type: 'snowflake',
        snowflake_db: infra.snowflakeDb,
        snowflake_schema: infra.snowflakeSchema,
        snowflake_table: versionMeta?.snowflake_table || '',
        destination_uri: '',
      });
    }
  }

  if (!deliveries.length) warnings.push('Org is not delivery ready for this product');

  return {
    source,
    deliveries,
    warnings,
    isReady: deliveries.length > 0,
    historyModes: history.modes,
    latestDeliveredVersion: history.latestVersion,
  };
};

export const buildProductsByReleaseType = (allDataAssets) => {
  const base = { Contact: [], Digital: [] };

  for (const a of allDataAssets) {
    if (a?.usage !== 'External') continue;
    if (a?.assetType !== 'Standard Delivery') continue;

    const rel = a?.release;
    if (rel !== 'Contact' && rel !== 'Digital') continue;

    const versions = Array.isArray(a.versions) ? a.versions : [];
    const latest = versions.length ? versions[versions.length - 1] : null;

    base[rel].push({
      dataAssetId: a._id || a.data_asset_id || a.id,
      name: a.name,
      versions: versions.map((v) => ({
        version: v.version,
        timestamp_utc: v.timestamp_utc,
        snowflake_table: v.snowflake_table,
        s3_locations: Array.isArray(v.s3_locations) ? v.s3_locations : [],
      })),
      latestVersion: latest?.version || '',
      latestTimestampUtc: latest?.timestamp_utc,
    });
  }

  base.Contact.sort((x, y) => String(x.name).localeCompare(String(y.name)));
  base.Digital.sort((x, y) => String(x.name).localeCompare(String(y.name)));
  return base;
};

export const buildReadiness = (selectedRelease) => {
  if (!selectedRelease) return { confirmed: 0, total: 0, pct: 0 };

  const contactItems = selectedRelease?.contact?.enabled ? (selectedRelease?.contact?.items || []) : [];
  const digitalItems = selectedRelease?.digital?.enabled ? (selectedRelease?.digital?.items || []) : [];
  const items = [...contactItems, ...digitalItems];
  const total = items.length;
  const confirmed = items.filter((x) => x?.confirmed).length;
  const pct = total ? Math.round((confirmed / total) * 100) : 0;

  return { confirmed, total, pct };
};

export const getVersionMetaFromAssetMap = (assetMap, releaseType, dataAssetId, version) => {
  const product = assetMap.get(String(dataAssetId));
  if (!product) return null;
  const versions = Array.isArray(product.versions) ? product.versions : [];
  return versions.find((v) => v.version === version) || null;
};



export const buildRecipientDialogRows = ({
  recipientDialogOpen,
  recipientDialogProduct,
  recipientDialogReleaseType,
  allOrgs,
  getVersionMeta,
  assetMap,
}) => {
  if (!recipientDialogOpen || !recipientDialogProduct || !recipientDialogReleaseType) return [];

  const rows = [];

  for (const org of allOrgs) {
    const enabledProducts = Array.isArray(org?.products) ? org.products : [];
    if (!enabledProducts.includes(recipientDialogProduct.product)) continue;

    const versionMeta = getVersionMeta(
      assetMap,
      recipientDialogReleaseType,
      recipientDialogProduct.data_asset_id,
      recipientDialogProduct.version
    );

    const plan = deriveDeliveryPlan({
      org,
      planned: recipientDialogProduct,
      versionMeta,
    });

    rows.push({
      id: `${org.organization_id}:${recipientDialogProduct.product}`,
      organization_id: org.organization_id,
      org_name: org.name,
      current_version: plan.latestDeliveredVersion || '',
      planned_version: recipientDialogProduct.version || '',
      planned_targets: plan.deliveries.map((d) => d.mode).join(', ') || 'none',
      source: plan.source,
      ready: plan.isReady,
      warnings: plan.warnings.join(' | '),
    });
  }

  return rows.sort((a, b) => String(a.org_name).localeCompare(String(b.org_name)));
};